import { memo, useEffect, useMemo, useRef, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { useNavigate } from "@tanstack/react-router"
import { EditorContent } from "@tiptap/react"
import {
	IconCubeOutline18 as Box,
	IconChevronRightOutline18 as ChevronRight,
	IconUserOutline18 as User,
	IconUserXmarkOutline18 as UserX,
} from "nucleo-ui-outline-18"
import { useMedia } from "react-use"
import { flatMap, groupBy } from "remeda"
import showdown from "showdown"
import { z } from "zod"

import {
	Alert,
	Button,
	Dialog,
	Form,
	FormTextarea,
	IssueStatusIcon,
	Loader,
	SubmitButton,
	useZodForm,
} from "@productlane/ui"
import {
	InternalDialogFooter,
	InternalDialogTitle,
} from "@productlane/ui/src/components/internal/dialog"
import { LinearIcon } from "@productlane/ui/src/icons/linear-logo"
import { queries } from "@productlane/zero/queries"

import type { MoreOptionValues } from "./create-issue-more-options"
import type { CreateIssuePicker } from "./create-issue-shortcuts"
import type { LinearTemplate } from "./template-select"
import type { DataOptionType } from "@/components/popover-button"
import type { RouterOutputs } from "@/lib/api"
import { unwrapBlockImages } from "@/components/editor/unwrap-block-images"
import { useIssueDescriptionEditor } from "@/components/editor/use-issue-description-editor"
import { LabelSelect, labelsForTeam } from "@/components/label-select"
import { PopoverButton } from "@/components/popover-button"
import { toast } from "@/components/toast"
import { toggleLabelId } from "@/components/toggle-label-id"
import { focusModeWidth } from "@/constants"
import { issueStatusOrder, LinearPriorityIcon } from "@/constants"
import { useShortcut } from "@/lib"
import { analytics } from "@/lib/analytics"
import { api } from "@/lib/api"
import { useSingleFlight } from "@/lib/hooks/use-single-flight"
import { getProjectIcon } from "@/lib/projects"
import { useSession } from "@/lib/session"
import { useZeroWorkspace } from "@/lib/zero"
import { resolveDefaultTeamId } from "../threads/hooks/resolve-default-team-id"
import { useSyncedTeams } from "../threads/hooks/useSelectedTeams"
import {
	AGENT_MENU_VALUE,
	agentValue,
	assigneeAndAgentOptions,
	findSelf,
	parseAssigneeChoice,
} from "./assignee-options"
import {
	CHIP_CLASS,
	CreateIssueMoreOptions,
	useMoreOptionChoices,
} from "./create-issue-more-options"
import { isMoreOptionField } from "./create-issue-more-options-fields"
import { CreateIssueQuickSuggestions } from "./create-issue-quick-suggestions"
import {
	canOpenPicker,
	createIssueShortcutKeys,
	findSelfMember,
	ShortcutTooltipLabel,
	useCreateIssueShortcuts,
} from "./create-issue-shortcuts"
import { isSeededDescription } from "./description-seed"
import { TemplateSelect } from "./template-select"
import { useSeedPriorityOnOpen } from "./use-seed-priority-on-open"
import { useSeedTitleOnOpen } from "./use-seed-title-on-open"

export type PartialIssueCreate = RouterOutputs["linear"]["createIssue"]

export interface CreateIssueDialogProps {
	isOpen: boolean
	onOpenChange: (
		value: boolean,
		created?: PartialIssueCreate & {
			internalUpdate?: {
				isVisible?: boolean
				notUpdateVisibility?: boolean
			}
		},
	) => void
	title?: string
	// if this is defined it means a conversion
	tagId?: string
	highlighted?: string
	defaultStatus?: string
	defaultPriority?: number
}

const markdownConverter = new showdown.Converter({
	simpleLineBreaks: true,
	simplifiedAutoLink: true,
	openLinksInNewWindow: true,
})

// Seed text is plaintext plus markdown attachment links/images; escape raw HTML before the markdown pass.
function plainTextToEditorHtml(text: string) {
	const escaped = text.replace(/&/g, "&amp;").replace(/</g, "&lt;")
	return unwrapBlockImages(markdownConverter.makeHtml(escaped))
}

export const CreateLinearIssueInputSchema = z.object({
	id: z.string().optional(),
	teamId: z.string().min(1),
	title: z
		.string()
		.max(512, { message: "Title cannot be longer than 512 characters." }),
	description: z.string().optional(),
	assigneeId: z.string().optional(),
	stateId: z.string(),
	priority: z.string(),
	labelIds: z.array(z.string()),
	projectId: z.string().optional(),
	agentId: z.string().optional(),
	templateId: z.string().optional(),
	dueDate: z.string().optional(),
	cycleId: z.string().optional(),
	estimate: z.string().optional(),
	projectMilestoneId: z.string().optional(),
	releaseId: z.string().optional(),
	parent: z
		.object({ id: z.string(), identifier: z.string(), title: z.string() })
		.optional(),
	links: z.array(z.object({ url: z.string(), title: z.string().optional() })),
})

export const CreateIssueDialog = memo(
	({
		isOpen,
		tagId,
		onOpenChange,
		title,
		highlighted,
		defaultStatus,
		defaultPriority,
	}: CreateIssueDialogProps) => {
		const [projects] = useQuery(queries.projects())
		const workspace = useZeroWorkspace()
		const navigate = useNavigate()
		const { session } = useSession()

		const selectedTeams = useSyncedTeams()
		const defaultTeamId = resolveDefaultTeamId(
			workspace?.defaultLinearTeamId,
			selectedTeams,
		)

		const seededDescriptionHtml = useMemo(
			() => (highlighted ? plainTextToEditorHtml(highlighted) : ""),
			[highlighted],
		)

		const { data: labels } = api.linear.labels.useQuery(undefined, {
			cacheTime: Infinity,
			staleTime: Infinity,
			enabled: isOpen,
		})

		const form = useZodForm({
			schema: CreateLinearIssueInputSchema,
			mode: "onChange",
			defaultValues: {
				teamId: defaultTeamId,
				title,
				description: seededDescriptionHtml,
				assigneeId: undefined,
				stateId: undefined,
				priority: undefined,
				labelIds: [],
				projectId: undefined,
				agentId: undefined,
				templateId: undefined,
				links: [],
			},
		})

		const editor = useIssueDescriptionEditor({
			placeholder: "Add description...",
			onUpdate: (html, isEmpty) =>
				form.setValue("description", isEmpty ? "" : html),
		})

		// Snapshot of the auto-populated body, so a template can replace it while
		// anything the user typed themselves survives.
		const seededEditorHtmlRef = useRef("")

		// Seed the rich editor from a passed-in highlight; setContent doesn't emit onUpdate, so sync the form value too.
		useEffect(() => {
			if (!isOpen || !editor || editor.isDestroyed) return
			if (!isSeededDescription(editor.getHTML(), seededEditorHtmlRef.current)) {
				return
			}
			editor.commands.setContent(seededDescriptionHtml)
			seededEditorHtmlRef.current = editor.getHTML()
			form.setValue("description", seededDescriptionHtml)
		}, [seededDescriptionHtml, editor, form, isOpen])

		useEffect(() => {
			if (isOpen || !editor || editor.isDestroyed) return
			editor.commands.clearContent()
			seededEditorHtmlRef.current = ""
		}, [isOpen, editor])

		const { data: templates } = api.linear.templates.useQuery(undefined, {
			cacheTime: Infinity,
			staleTime: Infinity,
			enabled: isOpen,
		})

		// Only offer templates whose target team is visible in the workspace's Linear settings.
		const selectableTemplates = useMemo(() => {
			const selectedIds = new Set((selectedTeams || []).map(({ id }) => id))
			return (templates || []).filter(
				(template) => !template.teamId || selectedIds.has(template.teamId),
			)
		}, [templates, selectedTeams])

		const { data: priorities } = api.linear.priorities.useQuery(undefined, {
			cacheTime: Infinity,
			staleTime: Infinity,
			enabled: isOpen,
		})

		const createIssueMutation = api.linear.createIssue.useMutation()
		const convertToIssueMutation = api.linear.convertTagToIssue.useMutation()

		const teamOptions = useMemo(
			() =>
				(selectedTeams || []).map(({ icon, color, id, name }) => {
					const Icon = getProjectIcon(icon)
					return {
						label: (
							<div className="flex items-center">
								<Icon className="h-4 w-4 flex-none" style={{ color }} />
								<span className="ml-2">{name}</span>
							</div>
						),
						value: id,
					}
				}),
			// eslint-disable-next-line react-hooks/exhaustive-deps
			[selectedTeams, workspace?.selectedTeamIds],
		)

		const teamNameById = useMemo(
			() =>
				Object.fromEntries(
					(selectedTeams || []).map(({ id, name }) => [id, name]),
				),
			[selectedTeams],
		)

		const teamId = form.watch("teamId")
		const assigneeId = form.watch("assigneeId")
		const stateId = form.watch("stateId")
		const labelIds = form.watch("labelIds")
		const priority = form.watch("priority")
		const projectId = form.watch("projectId")
		const agentId = form.watch("agentId")
		const watchedTitle = form.watch("title")
		const watchedDescription = form.watch("description")
		const isTeamVisible = selectedTeams.some(({ id }) => id === teamId)

		useEffect(() => {
			if (!isTeamVisible && defaultTeamId) {
				form.setValue("teamId", defaultTeamId)
			}
		}, [isTeamVisible, defaultTeamId, form])

		const { data: states } = api.linear.workflowStates.useQuery(
			{
				teamId,
			},
			{
				cacheTime: Infinity,
				staleTime: Infinity,
				enabled: isOpen && isTeamVisible,
			},
		)

		const { data: members } = api.linear.members.useQuery(
			{
				teamId,
			},
			{
				cacheTime: Infinity,
				staleTime: Infinity,
				enabled: isOpen && isTeamVisible,
			},
		)

		const { data: agents } = api.linear.agents.useQuery(undefined, {
			cacheTime: Infinity,
			staleTime: Infinity,
			enabled: isOpen,
		})

		const { data: teamIssueSettings } = api.linear.teamIssueSettings.useQuery(
			{ teamId },
			{
				cacheTime: Infinity,
				staleTime: Infinity,
				enabled: isOpen && !!teamId,
				retry: false,
			},
		)

		const priorityOptions = useMemo(
			() =>
				(priorities || []).map(({ label, priority }) => {
					const Icon = LinearPriorityIcon[priority]
					return {
						label: (
							<div className="flex items-center">
								{!!Icon && (
									<Icon className="h-4 min-h-4 w-4 min-w-4 flex-none" />
								)}
								<span className="ml-2 truncate">{label}</span>
							</div>
						),
						value: priority.toString(),
					}
				}),
			[priorities],
		)

		const workflowStatesOptions = useMemo(() => {
			const groupedStates = groupBy(states || [], (state) => state.type)

			// Triage leads, as in Linear, so it takes 0 and Backlog takes 1.
			const sortedGroups = [
				"triage",
				...issueStatusOrder.filter((type) => type !== "triage"),
			].map((type) => groupedStates[type] || [])

			return flatMap(sortedGroups, (group) =>
				group
					.sort((a, b) => a.position - b.position)
					.map(({ id, name, color, type }) => ({
						label: (
							<div className="ml-0.5 flex items-center">
								<IssueStatusIcon
									color={color}
									className="h-3.5 w-3.5 flex-none"
									status={type}
								/>
								<span className="ml-2 truncate">{name}</span>
							</div>
						),
						value: id,
					})),
			)
		}, [states])

		const assigneeOptions = useMemo(
			() =>
				assigneeAndAgentOptions({
					members,
					agents,
					selfEmail: session?.email,
					assigneeId,
					delegateId: agentId,
				}),
			[members, agents, session?.email, assigneeId, agentId],
		)

		const agentIds = useMemo(
			() => new Set((agents ?? []).map(({ id }) => id)),
			[agents],
		)

		const teamProjects = useMemo(
			() =>
				(projects || []).filter(
					({ linearProjectId, state, linearTeamIds }) =>
						linearProjectId &&
						(linearTeamIds ?? []).includes(teamId) &&
						!["closed", "completed", "cancelled"].includes(state ?? ""),
				),
			[projects, teamId],
		)
		const suggestionProjects = useMemo(
			() =>
				teamProjects.map(({ linearProjectId, name, icon, color }) => ({
					id: linearProjectId!,
					name,
					icon,
					color,
				})),
			[teamProjects],
		)
		const teamLabels = useMemo(
			() => labelsForTeam(labels ?? [], teamId),
			[labels, teamId],
		)

		const projectOptions = useMemo(
			() =>
				teamProjects
					.map(({ linearProjectId, icon, name, color }) => {
						const Icon = getProjectIcon(icon)
						if (linearProjectId) {
							return {
								label: (
									<div className="flex items-center">
										{!!Icon && (
											<Icon
												className="h-4 w-4 flex-none"
												style={{ color: color ?? "" }}
											/>
										)}
										<span className="ml-2 truncate">{name}</span>
									</div>
								),
								value: linearProjectId,
							}
						}
					})
					.filter(Boolean) as DataOptionType[],
			[teamProjects],
		)

		// Set when a template switches the team; consumed by the team-change effects
		// below so they don't wipe the template's own status/assignee/project/labels.
		const pendingTemplateRef = useRef<LinearTemplate | null>(null)
		// The switched-in team's states load async; hold the template's stateId so the
		// default-state effect applies it instead of resetting to triage/backlog.
		const pendingStateIdRef = useRef<string | null>(null)

		useEffect(() => {
			if (priorities?.[0] && !form.getValues("priority")) {
				form.setValue("priority", priorities[0].priority.toString() ?? "")
			}
		}, [priorities, form])

		// Team-scoped fields belong to a single team; on a manual team change drop stale ones so
		// Linear does not reject them. When a template switched the team, apply its team-bound
		// fields here instead of clearing them.
		useEffect(() => {
			form.setValue("cycleId", undefined)
			form.setValue("estimate", undefined)
			const template = pendingTemplateRef.current
			if (template) {
				pendingTemplateRef.current = null
				form.setValue("assigneeId", template.data.assigneeId ?? "")
				form.setValue("projectId", template.data.projectId ?? "")
				form.setValue("labelIds", template.data.labelIds ?? [])
				if (template.data.delegateId) {
					form.setValue("agentId", template.data.delegateId)
				}
				return
			}
			form.setValue("assigneeId", "")
			form.setValue("projectId", "")
			form.setValue("labelIds", [])
		}, [teamId, form])

		// A milestone belongs to one project.
		useEffect(() => {
			form.setValue("projectMilestoneId", undefined)
		}, [projectId, form])

		useEffect(() => {
			if (!states?.length) return
			if (pendingStateIdRef.current) {
				const templateStateId = pendingStateIdRef.current
				pendingStateIdRef.current = null
				if (states.some(({ id }) => id === templateStateId)) {
					form.setValue("stateId", templateStateId)
					return
				}
			}
			const defaultStateId = defaultStatus
				? states.find(({ type }) => type === defaultStatus)?.id
				: states.find(({ type }) => type === "triage" || type === "backlog")?.id
			form.setValue("stateId", defaultStateId || states[0]?.id || "")
		}, [states, form, defaultStatus])

		useSeedTitleOnOpen(form, isOpen, title)
		useSeedPriorityOnOpen(form, isOpen, defaultPriority, priorities)

		// One picker open at a time, so a shortcut can open it and a digit can pick in it.
		const [openPicker, setOpenPicker] = useState<CreateIssuePicker | null>(null)
		const pickerState = (picker: CreateIssuePicker) => ({
			open: openPicker === picker,
			onOpenChange: (open: boolean) =>
				setOpenPicker((current) =>
					open ? picker : current === picker ? null : current,
				),
		})
		useEffect(() => {
			if (!isOpen) setOpenPicker(null)
		}, [isOpen])

		const moreOptionChoices = useMoreOptionChoices({
			isOpen,
			teamId,
			projectId: projectId || undefined,
			settings: teamIssueSettings,
		})

		const dialogRef = useRef<HTMLDivElement>(null)
		const isLarge = useMedia(focusModeWidth)
		useCreateIssueShortcuts(isOpen, dialogRef, (shortcut) => {
			if (shortcut === "assignSelf") {
				// Nothing to set until the team's members load, or if you aren't one of them.
				const self = findSelfMember(members, session?.email)
				if (self) form.setValue("assigneeId", self.id)
				return
			}
			const choiceCounts = {
				status: workflowStatesOptions.length,
				priority: priorityOptions.length,
				assignee: assigneeOptions.length,
				project: projectOptions.length,
				labels: labelsForTeam(labels ?? [], teamId).length,
				cycle: moreOptionChoices.cycle.options.length,
				estimate: moreOptionChoices.estimate.options.length,
				milestone: moreOptionChoices.milestone.options.length,
				release: moreOptionChoices.release.options.length,
			}
			if (canOpenPicker(shortcut, choiceCounts)) setOpenPicker(shortcut)
		})

		const applyTemplate = (template: LinearTemplate | null) => {
			if (!template) {
				form.setValue("templateId", "")
				return
			}

			form.setValue("templateId", template.id)

			// Team-agnostic fields always apply.
			if (template.data.priority !== undefined) {
				form.setValue("priority", template.data.priority.toString())
			}
			if (template.data.title) {
				const currentTitle = form.getValues("title")?.trim() ?? ""
				form.setValue(
					"title",
					currentTitle
						? `${currentTitle} ${template.data.title}`
						: template.data.title,
				)
			}
			if (template.data.descriptionHtml && editor && !editor.isDestroyed) {
				if (
					isSeededDescription(editor.getHTML(), seededEditorHtmlRef.current)
				) {
					editor.commands.setContent(template.data.descriptionHtml)
					seededEditorHtmlRef.current = editor.getHTML()
					form.setValue("description", editor.getHTML())
				} else {
					editor
						.chain()
						.insertContentAt(
							editor.state.doc.content.size,
							template.data.descriptionHtml,
						)
						.run()
				}
			}

			// Team-bound fields (status, assignee, project, labels) only make sense for the
			// template's own team. If that team is available in the workspace's Linear settings,
			// switch to it and let the team-change effects apply the fields; otherwise skip them.
			if (template.teamId && template.teamId !== teamId) {
				const canSwitchTeam = (selectedTeams || []).some(
					({ id }) => id === template.teamId,
				)
				if (canSwitchTeam) {
					pendingTemplateRef.current = template
					pendingStateIdRef.current = template.data.stateId ?? null
					form.setValue("teamId", template.teamId)
				} else {
					toast.info({
						title: "Template team not applied",
						description:
							"This template is for another team. Its status, assignee, project, and labels were skipped.",
						icon: LinearIcon,
					})
				}
				return
			}

			if (template.data.stateId) {
				form.setValue("stateId", template.data.stateId)
			}
			if (template.data.assigneeId) {
				form.setValue("assigneeId", template.data.assigneeId)
			}
			if (template.data.delegateId) {
				form.setValue("agentId", template.data.delegateId)
			}
			if (template.data.projectId) {
				form.setValue("projectId", template.data.projectId)
			}
			if (template.data.labelIds) {
				form.setValue("labelIds", template.data.labelIds)
			}
		}

		// Apply the preselected team's default Linear template on open, once states are
		// loaded so its status is not clobbered by the default-state effect.
		const autoAppliedTemplateRef = useRef(false)
		useEffect(() => {
			if (!isOpen) {
				autoAppliedTemplateRef.current = false
				return
			}
			if (autoAppliedTemplateRef.current || tagId) return
			if (!states || !templates) return
			if (form.getValues("templateId")) return
			const activeTeamId = form.getValues("teamId")
			const defaultTemplateId = (selectedTeams || []).find(
				({ id }) => id === activeTeamId,
			)?.defaultTemplateForMembersId
			if (!defaultTemplateId) return
			const template = selectableTemplates.find(
				({ id }) => id === defaultTemplateId,
			)
			if (!template) return
			autoAppliedTemplateRef.current = true
			applyTemplate(template)
			if (defaultPriority !== undefined) {
				form.setValue("priority", defaultPriority.toString())
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [isOpen, states, templates, selectableTemplates, selectedTeams])

		// The dialog stays open until Linear answers, so a second ⌘↵ would create a duplicate.
		const onIssueCreate = useSingleFlight(async () => {
			const values = form.getValues()
			// A blank title is generated server-side from the description, so only both empty blocks.
			if (!values.title.trim() && !editor?.getText().trim()) {
				toast.error({ title: "Title can't be empty" })
				return
			}
			// Only delegate to an agent the picker actually offers; a template can carry a
			// delegate id the token can't delegate to, which Linear rejects on create.
			const delegateId =
				values.agentId && agentIds.has(values.agentId)
					? values.agentId
					: undefined
			try {
				if (tagId) {
					const data = await convertToIssueMutation.mutateAsync({
						tagId,
						teamId: values.teamId,
						title: values.title,
						description: values.description,
						assigneeId: values.assigneeId,
						delegateId,
						stateId: values.stateId,
						priority: Number(values.priority),
						labelIds: values.labelIds,
						projectId: values.projectId,
						...moreOptionsInput(values),
					})
					warnAboutFailedLinks(data.failedLinkUrls)
					onOpenChange(false, {
						id: tagId,
						identifier: "",
						priorityLabel: "",
						state: {
							type: values.stateId,
							color: null,
							name: null,
						},
						team: { id: values.teamId },
						title: data.title,
						url: "",
						dueDate: null,
						projectId: undefined,
						failedLinkUrls: [],
					})

					toast.success({
						title: "Converted to issue",
						descriptionAction: {
							label: `${data.identifier}: ${data.title}`,
							callback: () => window.open(data.url, "_blank"),
						},
					})
					analytics.track("convert-opportunity-to-issue")
				} else {
					const data = await createIssueMutation.mutateAsync({
						teamId: values.teamId,
						title: values.title,
						descriptionFormat: "html",
						description: values.description,
						assigneeId: values.assigneeId,
						stateId: values.stateId,
						priority: Number(values.priority),
						labelIds: values.labelIds,
						projectId: values.projectId,
						delegateId,
						templateId: values.templateId || undefined,
						...moreOptionsInput(values),
					})
					warnAboutFailedLinks(data.failedLinkUrls)
					onOpenChange(false, {
						...data,
					})

					toast.success({
						title: "Issue created",
						descriptionAction: {
							label: `${data.identifier}: ${data.title}`,
							callback: () => window.open(data.url, "_blank"),
						},
					})
					analytics.track("create-issue")
				}
			} catch (error) {
				toast.error({
					title: tagId
						? "Could not convert to issue"
						: "Could not create issue",
					description:
						error instanceof Error && error.message
							? error.message
							: "Something went wrong. Please try again.",
					icon: LinearIcon,
				})
				return
			}

			form.reset()
			if (editor && !editor.isDestroyed) {
				editor.commands.clearContent()
			}
			form.setValue("templateId", "")

			if (values.priority) {
				form.setValue("priority", priorities?.[0]?.priority.toString() ?? "")
			}

			if (values.stateId) {
				form.setValue(
					"stateId",
					states?.find(({ type }) => type === "triage" || type === "backlog")
						?.id ||
						states?.[0]?.id ||
						"",
				)
			}
		})

		useShortcut(
			"meta+enter, ctrl+enter",
			(e) => {
				e.preventDefault()
				if (isOpen) {
					// eslint-disable-next-line @typescript-eslint/no-floating-promises
					form.handleSubmit(async () => {
						try {
							await onIssueCreate()
						} catch (error) {
							console.error("Error handling form submission:", error)
						}
					})()
				}
			},
			{
				// The dialog mounts inside a Radix overlay, which the shared guard treats
				// as a reason to swallow every key.
				skipDefaultGuards: true,
				enableOnFormTags: true,
				enableOnContentEditable: true,
			},
		)

		return (
			<Dialog
				ref={dialogRef}
				isOpen={isOpen}
				onOpenChange={onOpenChange}
				className="m-2 max-h-[calc(100dvh-1rem)] p-0"
				scrollContainerClasses="mx-0 flex flex-col overflow-hidden p-0"
				position="right"
				withOverlay={false}
				onCloseAutoFocus={(event) => event.preventDefault()}
				onInteractOutside={(event) => event.preventDefault()}
				// Below focus-mode width the thread sidebar is a modal whose scroll lock
				// swallows scrolling here unless this dialog becomes the top modal.
				modal={!isLarge}
			>
				<Form
					id="create-issue-form"
					form={form}
					className="flex min-h-0 flex-1 flex-col"
					onSubmit={async () => {
						await onIssueCreate()
					}}
				>
					{/* The footer sits outside the scroll area so Create stays reachable under a long description. */}
					{/* Stops wheel and touch at this scroller so a scroll lock left by the modal
					    thread sidebar on smaller screens cannot cancel them. */}
					<div
						className="min-h-0 flex-1 overflow-y-auto overscroll-contain"
						onWheel={(event) => event.stopPropagation()}
						onTouchMove={(event) => event.stopPropagation()}
					>
						<InternalDialogTitle className="sr-only">
							{tagId ? "Convert to Linear issue" : "Create Linear issue"}
						</InternalDialogTitle>
						<div className="flex flex-col">
							<div className="space-y-4 p-5">
								{/* Team + template */}
								{selectedTeams ? (
									selectedTeams.length === 0 ? null : (
										<div className="flex flex-wrap items-center gap-1.5">
											<PopoverButton
												variant="outline"
												className="dark:bg-gray-800 dark:hover:bg-gray-750"
												onChange={(value: string | null) =>
													form.setValue("teamId", value ?? "")
												}
												data={teamOptions}
												selected={teamId}
												placeholder="Select team..."
												numberShortcuts
											/>
											{!tagId && (
												<>
													<ChevronRight className="h-3 w-3 shrink-0 text-tertiary" />
													<TemplateSelect
														templates={
															templates ? selectableTemplates : templates
														}
														teamNameById={teamNameById}
														selectedTemplateId={form.watch("templateId")}
														onSelect={applyTemplate}
													/>
												</>
											)}
										</div>
									)
								) : (
									<Button variant="outline">
										<Loader className="h-4 w-4" />
									</Button>
								)}

								{/* Title */}
								<FormTextarea
									textSize="xl"
									className="w-full font-medium"
									variant="borderless"
									name="title"
									placeholder="Issue title"
									maxLength={513}
									autoFocus
								/>

								{/* Description */}
								<EditorContent
									editor={editor}
									className="min-h-[7.5rem] w-full text-sm text-primary [&_.ProseMirror]:min-h-[7.5rem]"
								/>

								{selectedTeams?.length === 0 && (
									<Alert>
										<div className="flex flex-col gap-3">
											<span className="text-secondary">
												It seems you don&apos;t have any Linear teams visible.
												Please select them in the Linear integration settings
												page or make them public in Linear.
											</span>
											<Button
												variant="outline"
												className="w-min"
												Icon={LinearIcon}
												onClick={() => {
													onOpenChange(false)
													void navigate({
														to: "/settings/integrations/linear",
													})
												}}
											>
												Open Linear integration
											</Button>
										</div>
									</Alert>
								)}
							</div>

							<div className="flex flex-col gap-2 p-5">
								<CreateIssueQuickSuggestions
									isOpen={isOpen}
									title={watchedTitle ?? ""}
									descriptionHtml={watchedDescription ?? ""}
									labels={teamLabels}
									projects={suggestionProjects}
									teamId={teamId}
									selectedLabelIds={labelIds}
									selectedProjectId={projectId}
									priorities={priorities ?? undefined}
									selectedPriority={priority ? Number(priority) : null}
									members={members ?? undefined}
									selectedAssigneeId={assigneeId}
									onAddLabel={(labelId) =>
										form.setValue(
											"labelIds",
											toggleLabelId(labels ?? [], labelIds, labelId),
										)
									}
									onSetProject={(id) => form.setValue("projectId", id)}
									onSetPriority={(value) =>
										form.setValue("priority", value.toString())
									}
									onSetAssignee={(id) => form.setValue("assigneeId", id)}
								/>
								<div className="flex flex-row flex-wrap items-center gap-2">
									{/* Issue Status */}
									<PopoverButton
										variant="outline"
										className={CHIP_CLASS}
										activePlaceholder
										onChange={(value: string | null) =>
											form.setValue("stateId", value ?? "")
										}
										data={workflowStatesOptions}
										selected={stateId}
										placeholder="Change status..."
										isLoading={!states}
										numberShortcuts
										tooltip={
											<ShortcutTooltipLabel
												label="Change status"
												picker="status"
											/>
										}
										{...pickerState("status")}
										searchShortcut={createIssueShortcutKeys("status")}
									/>

									{/* Assignee, with agents to delegate to */}
									<PopoverButton
										variant="outline"
										className={CHIP_CLASS}
										activePlaceholder
										onChange={(value: string | null) => {
											const choice = parseAssigneeChoice(value, {
												assigneeId,
												selfId: findSelf(members, session?.email)?.id,
											})
											if (choice.assigneeId !== undefined) {
												form.setValue("assigneeId", choice.assigneeId ?? "")
											}
											if (choice.delegateId !== undefined) {
												form.setValue("agentId", choice.delegateId ?? "")
											}
										}}
										data={assigneeOptions}
										selected={
											assigneeId || (agentId ? AGENT_MENU_VALUE : undefined)
										}
										selectedItems={[
											...(assigneeId ? [assigneeId] : []),
											...(agentId ? [agentValue(agentId)] : []),
										]}
										placeholder="Assign to..."
										name="Assign"
										icon={User}
										resetOption="No assignee"
										resetIcon={UserXIconWithMargin}
										isLoading={!members}
										numberShortcuts
										tooltip={
											<ShortcutTooltipLabel
												label="Select assignee"
												picker="assignee"
											/>
										}
										{...pickerState("assignee")}
										searchShortcut={createIssueShortcutKeys("assignee")}
									/>

									{/* Priorities */}
									<PopoverButton
										variant="outline"
										className={CHIP_CLASS}
										activePlaceholder
										onChange={(value: string | null) =>
											form.setValue("priority", value ?? "")
										}
										data={priorityOptions}
										selected={priority}
										placeholder="Change priority..."
										isLoading={!priorities}
										numberShortcuts
										tooltip={
											<ShortcutTooltipLabel
												label="Change priority"
												picker="priority"
											/>
										}
										{...pickerState("priority")}
										searchShortcut={createIssueShortcutKeys("priority")}
									/>

									{/* Projects */}
									<PopoverButton
										variant="outline"
										className={CHIP_CLASS}
										activePlaceholder
										onChange={(value: string | null) =>
											form.setValue("projectId", value ?? "")
										}
										data={projectOptions}
										selected={projectId}
										name="Project"
										placeholder="Add to project..."
										resetOption="No project"
										resetIcon={Box}
										icon={Box}
										noResultsLabel="No projects found"
										numberShortcuts
										tooltip={
											<ShortcutTooltipLabel
												label="Add to project"
												picker="project"
											/>
										}
										{...pickerState("project")}
										searchShortcut={createIssueShortcutKeys("project")}
									/>
									{/* Labels */}
									<LabelSelect
										labels={labels || []}
										selectedLabelIds={labelIds}
										teamId={teamId}
										variant="outline"
										onChange={(labelId: string) =>
											form.setValue(
												"labelIds",
												toggleLabelId(labels ?? [], labelIds, labelId),
											)
										}
										tooltip={
											<ShortcutTooltipLabel label="Add label" picker="labels" />
										}
										{...pickerState("labels")}
									/>

									<CreateIssueMoreOptions
										isOpen={isOpen}
										projectId={projectId || undefined}
										settings={teamIssueSettings}
										choices={moreOptionChoices}
										active={
											openPicker && isMoreOptionField(openPicker)
												? openPicker
												: null
										}
										onActiveChange={(field) =>
											setOpenPicker(
												(current) =>
													field ??
													(current && isMoreOptionField(current)
														? null
														: current),
											)
										}
										values={{
											dueDate: form.watch("dueDate"),
											cycleId: form.watch("cycleId"),
											estimate: form.watch("estimate"),
											projectMilestoneId: form.watch("projectMilestoneId"),
											releaseId: form.watch("releaseId"),
											parent: form.watch("parent"),
											links: form.watch("links") ?? [],
										}}
										onChange={(patch) => {
											for (const [key, value] of Object.entries(patch)) {
												form.setValue(
													key as keyof MoreOptionValues,
													value as never,
												)
											}
										}}
									/>
								</div>
							</div>
						</div>
					</div>

					<div className="flex-none px-5 py-2">
						<InternalDialogFooter>
							<SubmitButton
								disabled={selectedTeams.length === 0}
								isLoading={
									createIssueMutation.isLoading ||
									convertToIssueMutation.isLoading
								}
							>
								{tagId ? "Convert to" : "Create"} Linear issue
							</SubmitButton>
						</InternalDialogFooter>
					</div>
				</Form>
			</Dialog>
		)
	},
)

function moreOptionsInput(
	values: z.infer<typeof CreateLinearIssueInputSchema>,
) {
	return {
		dueDate: values.dueDate || undefined,
		cycleId: values.cycleId || undefined,
		estimate: values.estimate ? Number(values.estimate) : undefined,
		projectMilestoneId: values.projectMilestoneId || undefined,
		releaseIds: values.releaseId ? [values.releaseId] : undefined,
		parentId: values.parent?.id,
		links: values.links.length ? values.links : undefined,
	}
}

function warnAboutFailedLinks(failedLinkUrls: string[]) {
	if (failedLinkUrls.length === 0) return
	toast.error({
		title:
			failedLinkUrls.length === 1
				? "Linear did not add the link"
				: `Linear did not add ${failedLinkUrls.length} links`,
		description: failedLinkUrls.join(", "),
		icon: LinearIcon,
	})
}

const UserXIconWithMargin = () => {
	return <UserX className="ml-1 h-4 w-4" />
}
